import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  startOfMonth,
} from 'date-fns';
import { de } from 'date-fns/locale';
import { PageIntro } from '@/components/help/PageIntro';
import { LabelWithHint } from '@/components/help/LabelWithHint';
import { Button } from '@/components/ui/button';
import { getAllPages } from '@/lib/paginated-data';
import { socialApi, type SocialPost } from '@/lib/social-api';
import type { Campaign, MarketingActivity } from '@/types';
import { cn } from '@/lib/utils';
import { TAG_BG } from '@/lib/tag-palette';
import { platformMeta } from '@/lib/social-platforms';
import { ChevronLeft, ChevronRight, PenSquare } from 'lucide-react';

/** Date-only values are calendar dates; timestamps follow the user's local time. */
function calendarDate(value?: string | null): string | null {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : format(date, 'yyyy-MM-dd');
}

type LayerId = 'activities' | 'campaigns' | 'social' | 'mailchimp';

interface ContentCalendarPageProps {
  onNavigate: (path: string) => void;
}

export function ContentCalendarPage({ onNavigate }: ContentCalendarPageProps) {
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [layers, setLayers] = useState<Record<LayerId, boolean>>({
    activities: true,
    campaigns: true,
    social: true,
    mailchimp: false,
  });
  const [activities, setActivities] = useState<MarketingActivity[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const [loadError, setLoadError] = useState(false);
  const [mailchimpEvents, setMailchimpEvents] = useState<
    Array<{ id: string; title: string; sent_at: string | null; status: string }>
  >([]);

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const days = useMemo(() => {
    const rangeStart = startOfMonth(cursor);
    const start = addDays(rangeStart, -((rangeStart.getDay() + 6) % 7));
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [cursor]);

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const [actRes, campRes, socialPosts, mail] = await Promise.all([
        getAllPages<MarketingActivity>('/activities'),
        getAllPages<Campaign>('/campaigns'),
        socialApi.listPosts({ all: true }).catch(() => { setLoadError(true); return [] as SocialPost[]; }),
        socialApi.newsletterCalendar().catch(() => { setLoadError(true); return []; }),
      ]);
      setActivities(actRes);
      setCampaigns(campRes);
      setPosts(socialPosts);
      setMailchimpEvents(mail);
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (id: LayerId) => setLayers((prev) => ({ ...prev, [id]: !prev[id] }));

  const eventsForDay = (day: Date) => {
    const key = format(day, 'yyyy-MM-dd');
    const items: Array<{ id: string; label: string; color: string; href: string }> = [];

    if (layers.activities) {
      for (const a of activities) {
        const start = calendarDate(a.start_date);
        if (start === key) {
          items.push({
            id: `a-${a.id}`,
            label: a.title || 'Aktivität',
            color: TAG_BG.slate,
            href: `/activities/${a.id}`,
          });
        }
      }
    }
    if (layers.campaigns) {
      for (const c of campaigns) {
        const start = calendarDate(c.start_date);
        if (start === key) {
          items.push({
            id: `c-${c.id}`,
            label: c.name || 'Kampagne',
            color: TAG_BG.violet,
            href: `/campaigns/${c.id}`,
          });
        }
      }
    }
    if (layers.mailchimp) {
      for (const ev of mailchimpEvents) {
        const d = calendarDate(ev.sent_at);
        if (d === key) {
          items.push({
            id: `m-${ev.id}`,
            label: ev.title.slice(0, 32),
            color: TAG_BG.orange,
            href: '/composer',
          });
        }
      }
    }
    if (layers.social) {
      for (const p of posts) {
        for (const t of p.targets) {
          if (!t.scheduled_at && !t.published_at) continue;
          const d = calendarDate(t.published_at || t.scheduled_at);
          if (d === key) {
            items.push({
              id: `s-${t.id}`,
              label: `${t.platform}: ${(p.body || p.title || 'Post').slice(0, 28)}`,
              color: platformMeta(t.platform).dotClass,
              href: `/composer/${p.id}`,
            });
          }
        }
      }
    }
    return items;
  };

  return (
    <div className="w-full">
      <PageIntro
        title="Content-Kalender"
        description="Aktivitäten, Kampagnen und Social-Posts in einer Ansicht. Schalte Layer ein und aus, um den Fokus zu setzen."
        className="max-w-none"
        actions={
          <Button onClick={() => onNavigate('/composer')}>
            <PenSquare size={16} className="mr-1.5" /> Neuer Post
          </Button>
        }
      />

      {loadError && (
        <div role="alert" className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-danger/30 bg-danger/10 p-3 text-sm text-foreground">
          Kalenderdaten konnten nicht vollständig geladen werden.
          <Button variant="outline" size="sm" onClick={() => void load()}>Erneut laden</Button>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button aria-label="Vorheriger Monat" variant="outline" size="sm" onClick={() => setCursor(addDays(monthStart, -1))}>
            <ChevronLeft size={16} />
          </Button>
          <h2 className="min-w-[10rem] text-center text-lg font-semibold capitalize text-foreground">
            {format(cursor, 'MMMM yyyy', { locale: de })}
          </h2>
          <Button aria-label="Nächster Monat" variant="outline" size="sm" onClick={() => setCursor(addDays(monthEnd, 1))}>
            <ChevronRight size={16} />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setCursor(startOfMonth(new Date()))}>
            Heute
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <LabelWithHint label="Layer" hint="Mailchimp-Live-Sends erscheinen, wenn die Integration aktiv ist (bestehender Feed)." />
          {([
            ['activities', 'Aktivitäten'],
            ['campaigns', 'Kampagnen'],
            ['social', 'Social'],
            ['mailchimp', 'Mailchimp'],
          ] as const).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => toggle(id)}
              className={cn(
                'rounded-lg px-2.5 py-1 text-xs font-medium border',
                layers[id]
                  ? 'border-border bg-card-hover text-foreground dark:bg-primary-hover/30'
                  : 'border-border text-muted'
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid w-full grid-cols-7 gap-px overflow-hidden rounded-xl border border-border bg-card-hover">
        {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((d) => (
          <div key={d} className="bg-background px-2 py-2 text-center text-xs font-semibold text-muted dark:bg-card">
            {d}
          </div>
        ))}
        {days.map((day) => {
          const items = eventsForDay(day);
          const inMonth = isSameMonth(day, cursor);
          const key = format(day, 'yyyy-MM-dd');
          const expanded = expandedDays.has(key);
          return (
            <div
              key={key}
              aria-label={format(day, 'd. MMMM yyyy', { locale: de })}
              className={cn(
                'min-h-[110px] bg-card p-2 dark:bg-background',
                !inMonth && 'bg-background/80 dark:bg-card/50'
              )}
            >
              <div className={cn('text-xs font-medium', inMonth ? 'text-muted dark:text-foreground' : 'text-muted')}>
                {format(day, 'd')}
              </div>
              <div className="mt-1 space-y-1">
                {(expanded ? items : items.slice(0, 4)).map((ev) => (
                  <button
                    key={ev.id}
                    type="button"
                    onClick={() => onNavigate(ev.href)}
                    className={cn('block w-full truncate rounded px-1.5 py-0.5 text-left text-[10px] text-white', ev.color)}
                    title={ev.label}
                  >
                    {ev.label}
                  </button>
                ))}
                {items.length > 4 && (
                  <button
                    type="button"
                    aria-expanded={expanded}
                    className="text-[10px] text-muted hover:text-foreground"
                    onClick={() => setExpandedDays((previous) => {
                      const next = new Set(previous);
                      if (next.has(key)) next.delete(key);
                      else next.add(key);
                      return next;
                    })}
                  >
                    {expanded ? 'Weniger anzeigen' : `+${items.length - 4} mehr`}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
